import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Cookies from "universal-cookie";
import queryString from "query-string";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { get, post } from "../services/api";
import { useToast } from "../admin/components/ToastProvider";
import {
  EmployeeEmptyState,
  EmployeeErrorState,
  EmployeeLoadingState,
  EmployeePageHeader,
  EmployeeSurface,
} from "../employee/components/EmployeePage";
import { tokens as t } from "../theme/tokens";

const smileys = ["😢", "😔", "😐", "🙂", "😊"];

const EmployeeSurvey: React.FC = () => {
  const { employeeId, surveyId } = useParams();
  const { showToast } = useToast();
  const parsedToken = queryString.parse(window.location.search).access_token;
  const publicToken = typeof parsedToken === "string" ? parsedToken : "";

  const [questionList, setQuestionList] = useState<any[]>([]);
  const [employeeDetails, setEmployeeDetails] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (publicToken) localStorage.setItem("publicToken", publicToken);
    const cookieName = process.env.REACT_APP_PUBLIC_COOKIE_NAME || "";
    if (cookieName && publicToken) {
      new Cookies().set(cookieName, publicToken, { domain: ".paperlessnow.ca", path: "/survey" });
    }
  }, [publicToken]);

  useEffect(() => {
    if (!employeeId || !surveyId || !publicToken) {
      setError("This survey link is incomplete or has expired. Please contact your administrator for a fresh link.");
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all([
      get(`openEmployee/${employeeId}`, publicToken),
      get(`survey/getServices?type=getSurveyQuestions&employeeId=${employeeId}&surveyId=${surveyId}&access_token=${publicToken}`),
    ])
      .then(([employeeResponse, surveyResponse]: any[]) => {
        setEmployeeDetails(employeeResponse?.payload?.data || {});
        setQuestionList(surveyResponse?.payload?.data || []);
      })
      .catch(() => setError("We could not load this survey. The link may have expired; please contact your administrator."))
      .finally(() => setLoading(false));
  }, [employeeId, publicToken, surveyId]);

  const updateQuestion = (question: any, field: "AnswerValue" | "AnswerText", value: any) => {
    setQuestionList((current) =>
      current.map((item) =>
        item.surveyquestionid === question.surveyquestionid ? { ...item, [field]: value } : item
      )
    );
  };

  const optionsFor = (question: any, delimiter: string) =>
    (question?.surveyquestionprefilledoptions || "")
      .split(delimiter)
      .filter((option: string) => Boolean(option.trim()));

  const renderOptions = (question: any, questionIndex: number) => {
    switch (question.surveyquestiontypename) {
      case "Rank":
        return (
          <Box
            role="group"
            aria-label={`Rating for question ${questionIndex + 1}`}
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 1.25 }}
          >
            {(question.surveyquestionrankdescription || "").split(",").map((option: string, index: number) => {
              const selected = question.AnswerValue === index + 1;
              return (
                <Button
                  key={`${question.surveyquestionid}-${index}`}
                  type="button"
                  variant={selected ? "contained" : "outlined"}
                  aria-pressed={selected}
                  onClick={() => updateQuestion(question, "AnswerValue", index + 1)}
                  sx={{
                    minHeight: 92,
                    flexDirection: "column",
                    gap: 0.75,
                    px: 1.25,
                    bgcolor: selected ? t.color.primary : t.color.paper,
                  }}
                >
                  <Box component="span" aria-hidden="true" sx={{ fontSize: 24, lineHeight: 1 }}>
                    {smileys[index] || "•"}
                  </Box>
                  <Box component="span" sx={{ fontSize: "0.72rem", lineHeight: 1.35, color: selected ? t.color.onPrimary : t.color.inkText }}>
                    {option}
                  </Box>
                </Button>
              );
            })}
          </Box>
        );

      case "Comment":
        return (
          <TextField
            multiline
            minRows={4}
            name={`question-${question.surveyquestionid}`}
            inputProps={{ "aria-label": `Response to question ${questionIndex + 1}` }}
            placeholder="Enter your response"
            value={question.AnswerText || ""}
            onChange={(event) => updateQuestion(question, "AnswerText", event.target.value)}
          />
        );

      case "YesNo":
        return (
          <Stack role="group" aria-label={`Answer for question ${questionIndex + 1}`} direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            {["Yes", "No"].map((answer) => {
              const selected = question.AnswerText === answer;
              return (
                <Button
                  key={answer}
                  type="button"
                  variant={selected ? "contained" : "outlined"}
                  aria-pressed={selected}
                  onClick={() => updateQuestion(question, "AnswerText", answer)}
                  sx={{ justifyContent: "flex-start", minWidth: 150 }}
                >
                  {answer}
                </Button>
              );
            })}
          </Stack>
        );

      case "Dropdown":
        return (
          <TextField
            select
            label="Select an option"
            value={question.AnswerText || ""}
            onChange={(event) => updateQuestion(question, "AnswerText", event.target.value)}
            sx={{ maxWidth: 520 }}
          >
            <MenuItem value=""><em>Select an option</em></MenuItem>
            {optionsFor(question, ";").map((option: string, index: number) => (
              <MenuItem key={`${option}-${index}`} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        );

      case "Multiselect": {
        const options = optionsFor(question, ";");
        return (
          <Autocomplete
            multiple
            options={options}
            value={question.AnswerText ? question.AnswerText.split(";") : []}
            onChange={(_event, value) => updateQuestion(question, "AnswerText", value.join(";"))}
            renderInput={(params) => <TextField {...params} label="Select all that apply" />}
          />
        );
      }

      default:
        return null;
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const responses = questionList.map((question) => ({
      SurveyID: question.surveyid,
      SurveyQuestionID: question.surveyquestionid,
      AnswerValue: question.AnswerValue,
      AnswerText: question.AnswerText,
    }));

    setSubmitting(true);
    post("survey/postServices", {
      type: "saveSurveyResults",
      results: Object.values(responses),
      surveyId,
    })
      .then(() => {
        setSubmitted(true);
        showToast("Thanks—your survey responses have been submitted.");
      })
      .catch(() => showToast("We could not submit your responses. Please try again.", "error"))
      .finally(() => setSubmitting(false));
  };

  if (loading) return <EmployeeLoadingState message="Loading your survey…" />;
  if (error) return <EmployeeErrorState message={error} />;

  const employeeName = [employeeDetails.first_name, employeeDetails.last_name].filter(Boolean).join(" ");

  return (
    <>
      <EmployeePageHeader
        eyebrow="Employee survey"
        title={employeeName ? `Welcome, ${employeeName}` : "Employee survey"}
        description="Complete the questionnaire below, then submit your responses."
      />

      {submitted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your responses have been submitted successfully.
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          {questionList.map((question, index) => (
            <EmployeeSurface key={question.surveyquestionid || index}>
              <Box sx={{ p: { xs: 2.25, sm: 3 } }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box
                    aria-hidden="true"
                    sx={{
                      flex: "0 0 auto",
                      width: 30,
                      height: 30,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "50%",
                      bgcolor: t.color.mint,
                      color: t.color.data,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography
                    component="h2"
                    variant="body1"
                    sx={{ fontWeight: 600, pt: 0.25, "& p": { m: 0 } }}
                    dangerouslySetInnerHTML={{ __html: question.surveyquestiontext || "" }}
                  />
                </Stack>
                {renderOptions(question, index)}
              </Box>
            </EmployeeSurface>
          ))}

          {!questionList.length && (
            <EmployeeSurface>
              <EmployeeEmptyState title="No survey questions are available" description="Please contact your administrator if you expected a survey here." />
            </EmployeeSurface>
          )}

          {questionList.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 0.5 }}>
              <Button type="submit" variant="contained" size="large" disabled={submitting || submitted} sx={{ width: { xs: "100%", sm: "auto" } }}>
                {submitting ? "Submitting…" : submitted ? "Submitted" : "Submit responses"}
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default EmployeeSurvey;
